import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faTriangleExclamation, faMicrochip, faSolarPanel, faBolt, faBatteryFull, faArrowRight, faArrowsRotate, faArrowRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { getStatusMeta } from '../../utils/compatibilityStatus';
import TypeChips from '../TypeChips';
import { pln, dec2, int } from './format';
import SaveKitButton from './SaveKitButton';
import ZapiszDlaKlienta from './ZapiszDlaKlienta';
import { zapiszZestaw } from '../../lib/wybranyZestaw';
import PanelElektryczny from '../elektryka/PanelElektryczny';
import { usePodmianaProduktu } from './usePodmianaProduktu';
import PodsumowanieKosztow from './PodsumowanieKosztow';
import { magazynZPozycji } from '../../lib/wycena';
import { pojemnoscZestawuKwh } from '../../lib/podsumowanieZestawu';
import { useTranslation, Trans } from 'react-i18next';

// Nazwy kategorii ze słownika: karta.kategoria.<kategoria>.
const CAT_ICON = { panels: faSolarPanel, inverter: faBolt, battery: faBatteryFull, bms: faBolt };

// Linia specyfikacji pozycji zależnie od kategorii (dane z API — front nie liczy).
// `wart` tłumaczy wartości z bazy (fazy, napięcie) do wyświetlenia.
const specLine = (item, wart) => {
  const s = item.spec || {};
  if (item.category === 'inverter') return [s.power_kw != null ? `${dec2(s.power_kw)} kW` : null, wart(s.phase)].filter(Boolean).join(' · ');
  if (item.category === 'battery') return [s.capacity_kwh != null ? `${dec2(s.capacity_kwh)} kWh` : null, wart(s.voltage)].filter(Boolean).join(' · ');
  return [s.power_w != null ? `${int(s.power_w)} W` : null, s.efficiency != null ? `${dec2(s.efficiency)}%` : null].filter(Boolean).join(' · ');
};

/**
 * Czy sprzedawca ma MNIEJ sztuk, niż potrzebuje zestaw.
 *
 * Sedno problemu: pozycja „Moduły PV · 10 szt." obok „dostępność: 1" wygląda na
 * gotową do zamówienia, a nie jest. Ostrzegamy TYLKO gdy dostępność jest podana
 * liczbą — „Dostępny" czy „Na zamówienie" nie niosą sztuk i zgadywanie z nich
 * byłoby fałszywym alarmem przy każdej takiej ofercie.
 */
const zaMaloSztuk = (item) => {
  const potrzeba = Number(item.quantity) || 0;
  if (potrzeba <= 1) return false;
  const surowe = String(item.availability ?? '').trim();
  const liczba = surowe.replace(/\s*(szt\.?|sztuk\w*|pcs)\.?$/i, '').trim();
  if (!/^\d+$/.test(liczba)) return false;
  return parseInt(liczba, 10) < potrzeba;
};

// Miniatura z ikoną kategorii, gdy zdjęcia brak albo się nie wczytało (2026-10-29: sklep blokujący osadzanie
// i zaślepki `data:` ze scrapera dawały puste białe pole; backend wybiera już inne zdjęcie, to jest zapas).
const Miniatura = ({ src, category }) => {
  const [blad, setBlad] = useState(null);
  const pokaz = src && /^https?:\/\//.test(src) && blad !== src;
  return pokaz
    ? <img src={src} alt="" loading="lazy" onError={() => setBlad(src)} data-testid="miniatura" className="max-h-full max-w-full object-contain" />
    : <FontAwesomeIcon icon={CAT_ICON[category] || faSolarPanel} className="text-gray-200 text-xl" />;
};

// Nazwy z ofert bywają bardzo długie (moduły z wymiarami i złączem w nazwie): na karcie do ok. 50 znaków, pełna
// w podpowiedzi i na stronie modelu (runda 9, 2026-10-26: karty rozjeżdżały się w wąski słupek).
export const MAKS_NAZWY = 50
export const skrocNazwe = (nazwa) => {
  const n = String(nazwa || '').trim();
  return n.length > MAKS_NAZWY ? `${n.slice(0, MAKS_NAZWY).trimEnd()}…` : n;
};

const ItemRow = ({ item, onPodmien, onCofnij, trwa, mozliwyPowrot }) => {
  const { t } = useTranslation('asystent');
  const wart = (v) => (v ? t(`wartosci.${v}`, { defaultValue: v }) : v);
  return (
  <li className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
    {/* Miniatura produktu (img_url z najtańszej oferty); fallback: ikona kategorii */}
    <Link to={item.model_url} className="h-14 w-14 shrink-0 rounded-xl border border-gray-100 bg-white flex items-center justify-center overflow-hidden hover:border-blue-200 transition-colors" tabIndex={-1} aria-hidden="true">
      <Miniatura src={item.img_url} category={item.category} />
    </Link>
    <div className="min-w-0 flex-1">
      <p className="text-xs text-gray-400">
        {t(`karta.kategoria.${item.category}`, { defaultValue: item.category })}
        {item.quantity > 1 ? ` · ${t('karta.sztuk', { n: int(item.quantity) })}` : ''}
      </p>
      <Link to={item.model_url} title={item.model_name}
            className="font-medium text-sm text-gray-900 hover:text-blue-600 leading-snug block break-words">
        {skrocNazwe(item.model_name)}
      </Link>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
        <span className="text-xs text-gray-500">{specLine(item, wart)}</span>
        {item.category === 'panels' && item.spec?.types && (
          <TypeChips types={item.spec.types} />
        )}
      </div>
      <p className="text-xs text-gray-400 mt-1 flex items-center gap-1.5 flex-wrap">
        <span>{item.seller}{item.availability ? ` · ${t('karta.dostepnosc', { wartosc: t(`wspolne:dostepnosc.${item.availability}`, { defaultValue: item.availability }) })}` : ''}</span>
        {zaMaloSztuk(item) && (
          <span
            title={t('karta.za_malo_opis', { ma: item.availability, potrzeba: int(item.quantity) })}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-amber-50 text-amber-700 font-semibold cursor-help"
          >
            <FontAwesomeIcon icon={faTriangleExclamation} className="text-[10px]" />
            {t('karta.za_malo')}
          </span>
        )}
      </p>
    </div>
    <div className="text-right shrink-0">
      <p className="font-semibold text-sm text-gray-900 whitespace-nowrap">{pln(item.line_price_pln)}</p>
      {item.quantity > 1 && (
        <p className="text-[11px] text-gray-400 whitespace-nowrap">{t('karta.za_sztuke', { kwota: pln(item.unit_price_pln) })}</p>
      )}
      {/* Zmiana produktu i cofnięcie. Male, przy cenie — to akcja pomocnicza,
          nie glowne wezwanie karty. Strzalka powrotu pojawia sie DOPIERO po
          pierwszej podmianie: przycisk cofania bez czego cofac tylko myli. */}
      {onPodmien && (
        <div className="flex items-center justify-end gap-1 mt-1.5">
          {mozliwyPowrot && (
            <button
              type="button"
              onClick={onCofnij}
              title={t('karta.cofnij')}
              aria-label={t('karta.cofnij')}
              className="h-7 w-7 inline-flex items-center justify-center rounded-lg border border-gray-200
                         text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-colors"
            >
              <FontAwesomeIcon icon={faArrowRotateLeft} className="text-[11px]" />
            </button>
          )}
          <button
            type="button"
            onClick={onPodmien}
            disabled={!!trwa}
            title={t('karta.zmien')}
            aria-label={t('karta.zmien')}
            className="h-7 w-7 inline-flex items-center justify-center rounded-lg border border-gray-200
                       text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-colors
                       disabled:opacity-50 disabled:cursor-wait"
          >
            <FontAwesomeIcon icon={faArrowsRotate} className={`text-[11px] ${trwa ? 'fa-spin' : ''}`} />
          </button>
        </div>
      )}
    </div>
  </li>
  );
};

const PrefChips = ({ items, tone }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map((p) => (
      <span key={p} className={`inline-flex items-center rounded-md text-xs font-medium px-2 py-0.5 ${tone}`}>{p}</span>
    ))}
  </div>
);

// `klient` ({ id, nazwa }) tylko w trybie asystenta „dla klienta” (`?klient=`); bez niego karta jak dotąd.
// `onUzyj(wariant)`: zamiast przejścia do wykonawcy przycisk przenosi zestaw tam, skąd otwarto dobór (asystent w oknie
// nad Projektem 3D, uwaga przeglądu A5), z napisem `tekstUzyj`.
// `tytul`: nazwa karty zamiast etykiety silnika (np. „Najwyższa jakość” w zleceniu). `uproszczona`: w zleceniu
// („Kilka pytań”) bez „Oferta i Projekt” i bez zapisu zestawu, bo konto i tak powstaje przy wystawieniu zlecenia.
const VariantCard = ({ variant, requestPayload, powrot = null, indeks = 0, tytul = null, uproszczona = false, konstrukcja, montazWliczony = true, nazwaWstepna = '', klient = null, onUzyj = null, tekstUzyj = null }) => {
  const { t } = useTranslation('asystent');
  const navigate = useNavigate();
  // Podmiana pojedynczej pozycji liczona przez silnik — patrz usePodmianaProduktu.
  const {
    wariant: v, podmien, cofnij, mozliwyPowrot, trwaPodmiana, brakZamiennika,
  } = usePodmianaProduktu(variant, requestPayload);

  // Przejście do wyboru wykonawcy. Zestaw idzie i stanem routera, i sesją —
  // stan ginie przy odswiezeniu, a ten ekran ludzie zostawiaja otwarty.
  const doWykonawcy = () => {
    zapiszZestaw(v, { powrot });
    navigate('/potwierdzenie-zestawu', { state: { zestaw: v } });
  };
  const comp = v.compatibility;
  const meta = comp ? getStatusMeta(comp.status) : null;
  const sumy = v.totals || {};
  const s = v.summary || {};

  /**
   * Konstrukcja i montaż doliczane PO STRONIE FRONTU, nad kwotą z silnika.
   *
   * Silnik zna ceny produktów i dostawy — te pochodzą z ofert sklepów. Konstrukcja
   * i montaż (robocizna z kablami i zabezpieczeniami) to nasze szacunki (cenniki w `lib/konstrukcja`
   * i `config/cennikMontazu`),
   * więc doliczamy je tutaj i pokazujemy jako osobne wiersze. Użytkownik ma widzieć,
   * ile z „łącznego kosztu" to twarde ceny, a ile nasz szacunek.
   *
   * UWAGA NA BUDŻET: silnik filtruje zestawy po SWOJEJ sumie (produkty + dostawa),
   * więc zestaw mieszczący się w budżecie może po doliczeniu montażu ten budżet
   * przekroczyć. Dlatego łączna kwota jest opisana jako „z montażem", a nie udaje,
   * że to ta sama liczba, na której pracował dobór.
   */
  const mocKwp = s.panels_total_kwp || null;
  const mocModuluW = (v.items || []).find((i) => i.category === 'panels')?.spec?.power_w || null;

  // Dobór elektryczny wymaga OBU stron pary — bez falownika nie ma czego liczyć.
  const [elektrykaOtwarta, setElektrykaOtwarta] = useState(false);
  const idPanelu = (v.items || []).find((i) => i.category === 'panels')?.model_id;
  const idFalownika = (v.items || []).find((i) => i.category === 'inverter')?.model_id;
  const bms = v.bms || {};
  // Magazyn zestawu: montaż i marża za magazyn z ustawień firmy (runda 6).
  const magazyn = magazynZPozycji(v.items, s);

  // Parametry instalacji jako osobne kafelki, nie jedna szara linijka. To NAJWAŻNIEJSZA
  // informacja na karcie — po niej porównuje się warianty między sobą — a ginęła
  // w tekście tej samej wagi co reszta opisu. Kafelki są celowo wąskie: mają rzucać
  // się w oczy, a nie zabierać miejsce liście produktów.
  const summaryChips = [
    { klucz: 'kwp', icon: faSolarPanel, wartosc: dec2(s.panels_total_kwp), jednostka: 'kWp', obecne: s.panels_total_kwp != null },
    { klucz: 'kw', icon: faBolt, wartosc: dec2(s.inverter_power_kw), jednostka: 'kW', obecne: s.inverter_power_kw != null },
    { klucz: 'kwh', icon: faBatteryFull, wartosc: dec2(pojemnoscZestawuKwh(s, v.items)), jednostka: 'kWh', obecne: pojemnoscZestawuKwh(s, v.items) != null },
  ].filter((c) => c.obecne);

  return (
    <article className="min-w-0 bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-5 flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h3 className="font-bold text-lg text-gray-900">{tytul || v.variant_label_pl}</h3>
        </div>
        {/* Plakietka BEZ statusu „warunkowo": jego treść (czyli konkretne warunki)
            ma teraz własny, rozwijany kafelek niżej. Ostrzegawczy trójkąt na górze
            karty straszył, zanim ktokolwiek przeczytał, o co chodzi — a warunkiem
            bywa np. „wymagana aktualizacja oprogramowania falownika". Pozostałe
            statusy zostają: „kompatybilne" uspokaja, a „niekompatybilne" i „brak
            danych" muszą być widoczne od razu. */}
        {meta && comp.status !== 'conditional' && (
          <span className={`shrink-0 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${meta.tone}`}>
            <FontAwesomeIcon icon={meta.icon} className={meta.iconTone} />
            {comp.label_pl || meta.label}
          </span>
        )}
      </div>

      {/* ═══ FALOWNIK WYMAGAJĄCY OPTYMALIZATORÓW ═══
          Kwota w podsumowaniu NIE zawiera optymalizatorów mocy, bo nie mamy ich
          w katalogu, a SolarEdge bez nich nie działa. Przy instalacji na dwadzieścia
          modułów to kwota rzędu ceny samego falownika, więc przemilczenie jej
          zmieniałoby sens całej sumy. Wariant „najtańszy" w ogóle takich zestawów
          nie pokazuje (backend_api/kits/engine.py), tu ostrzegamy w pozostałych. */}
      {v.wymaga_optymalizatorow && (
        <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
          <FontAwesomeIcon icon={faTriangleExclamation} className="text-amber-500 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-800 leading-5">
            <Trans t={t} i18nKey="karta.optymalizatory" components={{ b: <strong /> }} />
          </p>
        </div>
      )}

      {/* Kafelki NA PEŁNEJ SZEROKOŚCI, poza wierszem z plakietką zgodności.
          W tamtej kolumnie miały 171 px na telefonie i łamały się do trzech
          osobnych wierszy. Zmierzone. */}
      {summaryChips.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {summaryChips.map((c) => (
            <span
              key={c.klucz}
              className="inline-flex items-center gap-1 rounded-lg bg-blue-50 border border-blue-200 px-1.5 py-1"
            >
              <FontAwesomeIcon icon={c.icon} className="text-blue-500 text-[11px]" />
              <span className="text-sm font-bold text-blue-900 leading-none tabular-nums">{c.wartosc}</span>
              <span className="text-[11px] font-semibold text-blue-600 leading-none">{c.jednostka}</span>
            </span>
          ))}
        </div>
      )}

      <ul className="mb-4">
        {(v.items || []).map((it) => (
          <ItemRow
            key={`${it.category}-${it.model_id}`}
            item={it}
            /* BMS jest POCHODNĄ magazynu, nie samodzielnym wyborem — podmiana
               osobno rozjechałaby go z modułem bateryjnym. */
            onPodmien={requestPayload && it.category !== 'bms' ? () => podmien(it.category) : null}
            onCofnij={cofnij}
            trwa={trwaPodmiana === it.category}
            mozliwyPowrot={mozliwyPowrot}
          />
        ))}
      </ul>

      {brakZamiennika && (
        <p className="mb-4 -mt-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          {t('karta.brak_zamiennika')}
        </p>
      )}

      {bms.required && (
        <div className={`mb-4 rounded-lg border p-3 ${bms.resolved ? 'border-blue-200 bg-blue-50' : 'border-amber-200 bg-amber-50'}`}>
          <p className={`text-xs font-semibold mb-1 flex items-center gap-1.5 ${bms.resolved ? 'text-blue-800' : 'text-amber-800'}`}>
            <FontAwesomeIcon icon={faMicrochip} />
            {bms.resolved
              ? (bms.price_pln != null
                ? t('karta.bms_z_cena', { nazwa: bms.model_name, kwota: pln(bms.price_pln) })
                : t('karta.bms', { nazwa: bms.model_name }))
              : t('karta.bms_brak')}
          </p>
          {bms.note_pl && <p className={`text-[11px] leading-relaxed ${bms.resolved ? 'text-blue-700' : 'text-amber-700'}`}>{bms.note_pl}</p>}
        </div>
      )}

      {/* Warunki kompatybilności ZWINIĘTE. Rozwinięta lista zajmowała kilka wierszy
          nad ceną i wyglądała na problem, choć zwykle to formalność w rodzaju
          „wymagana aktualizacja oprogramowania". Kto chce, rozwija. */}
      {comp && comp.conditions_pl && comp.conditions_pl.length > 0 && (
        <details className="mb-4 rounded-lg border border-amber-200 bg-amber-50 group">
          <summary className="px-3 py-2.5 text-xs font-semibold text-amber-800 flex items-center justify-between gap-2 cursor-pointer select-none list-none">
            <span className="flex items-center gap-1.5">
              <FontAwesomeIcon icon={faTriangleExclamation} className="text-amber-600" />
              {t('karta.warunki')}
              <span className="font-normal text-amber-600">({comp.conditions_pl.length})</span>
            </span>
            <FontAwesomeIcon icon={faChevronDown} className="text-amber-500 text-xs transition-transform group-open:rotate-180" />
          </summary>
          <div className="px-3 pb-3">
            <ul className="text-[11px] text-amber-700 space-y-0.5 list-disc list-inside">
              {comp.conditions_pl.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
            {comp.source_ids && comp.source_ids.length > 0 && (
              <p className="text-[10px] text-amber-600 mt-1.5">{t('karta.zrodlo', { ids: comp.source_ids.join(', ') })}</p>
            )}
          </div>
        </details>
      )}

      {(v.preferences_met?.length > 0 || v.preferences_missed?.length > 0) && (
        <div className="mb-4 flex flex-col gap-2">
          {v.preferences_met?.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] text-gray-400">{t('karta.spelnione')}</span>
              <PrefChips items={v.preferences_met} tone="bg-emerald-50 text-emerald-700" />
            </div>
          )}
          {v.preferences_missed?.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] text-gray-400">{t('karta.niespelnione')}</span>
              <PrefChips items={v.preferences_missed} tone="bg-gray-100 text-gray-500" />
            </div>
          )}
        </div>
      )}

      <PodsumowanieKosztow
        klasa="rounded-xl bg-gray-50 p-4 mb-4 mt-auto"
        produktyZl={sumy.products_pln}
        bmsZl={bms.resolved && bms.price_pln != null ? bms.price_pln : null}
        dostawaZl={sumy.shipping_pln}
        liczbaDostaw={sumy.deliveries_count}
        liczbaPalet={s.pallets_count}
        liczbaSprzedawcow={sumy.sellers_count}
        kwp={mocKwp}
        mocModuluW={mocModuluW}
        konstrukcja={konstrukcja}
        montazWliczony={montazWliczony}
        kwh={magazyn.kwh}
        magazynZl={magazyn.cenaZl}
        /* Karta ZAWSZE z rozbiciem: oglada ja firma, nie klient koncowy.
           Ustawienie „tylko cena koncowa" dotyczy dokumentu, ktory idzie dalej. */
        tylkoKwota={false}
      />

      {/* Dobór elektryczny — ZWINIĘTY, bo interesuje mniejszość (instalatorów).
          Panel liczy dopiero po rozwinięciu (`open`), żeby nie odpytywać backendu
          dla każdego z czterech wariantów przy każdym doborze zestawu. */}
      {idPanelu && idFalownika && !uproszczona && (
        <details className="mb-3 rounded-xl border border-blue-500 overflow-hidden group"
                 onToggle={(e) => setElektrykaOtwarta(e.target.open)}>
          <summary className="vv-projekt-blysk bg-blue-600 px-3 py-3 text-sm font-semibold text-white
                              flex items-center justify-between cursor-pointer select-none list-none
                              hover:bg-blue-500 transition-colors">
            <span className="flex items-center gap-2">
              <FontAwesomeIcon icon={faBolt} className="text-blue-100" />
              <span>
                {t('karta.projekt')}
                <span className="block text-[11px] font-normal text-blue-100">
                  {t('karta.projekt_opis')}
                </span>
              </span>
            </span>
            <FontAwesomeIcon icon={faChevronDown} className="text-blue-100 text-xs transition-transform group-open:rotate-180" />
          </summary>
          <div className="px-3 pb-3 pt-3">
            {elektrykaOtwarta && (
              <PanelElektryczny
                panelModelId={idPanelu}
                inverterModelId={idFalownika}
                mocDocelowaKwp={s.panels_total_kwp}
                items={v.items}
                summary={s}
                /* Skladniki kosztu w dol, zeby ostatnia strona dokumentu
                   pokazala DOKLADNIE te kwote co ta karta. */
                koszty={{
                  produktyZl: sumy.products_pln,
                  bmsZl: bms.resolved && bms.price_pln != null ? bms.price_pln : null,
                  dostawaZl: sumy.shipping_pln,
                  mocModuluW,
                  konstrukcja,
                  montazWliczony,
                  kwh: magazyn.kwh,
                  magazynZl: magazyn.cenaZl,
                }}
                /* Siatka wariantow jest dwukolumnowa: karty parzyste stoja po
                   lewej, wiec ich dymek wychodzi w lewo, zeby nie zakryl sasiada. */
                dymekDoLewej={indeks % 2 === 0}
              />
            )}
          </div>
        </details>
      )}

      {v.warnings_pl?.length > 0 && (
        <div className="text-[11px] text-gray-500 space-y-1">
          {v.warnings_pl.map((w, i) => (
            <p key={i} className="flex items-start gap-1.5">
              <FontAwesomeIcon icon={faTriangleExclamation} className="text-amber-400 mt-0.5 shrink-0" />
              <span>{w}</span>
            </p>
          ))}
        </div>
      )}
      {/* Tryb „dla klienta” (2026-10-11): jedno zakończenie, zapis i podpięcie pod klienta. Droga do wykonawcy
          i osobny zapis znikają, bo firma dobiera tu sprzęt dla swojego klienta, a nie szuka instalatora. */}
      {klient ? (
        <ZapiszDlaKlienta klient={klient} variant={v} requestPayload={requestPayload} nazwaWstepna={nazwaWstepna} />
      ) : (
        <>
          <button
            type="button"
            onClick={onUzyj ? () => onUzyj(v) : doWykonawcy}
            className="mt-3 w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-blue-600
                       text-white font-semibold hover:bg-blue-500 active:bg-blue-700 transition-colors"
          >
            {onUzyj ? tekstUzyj : t('karta.wybierz_zestaw')}
            <FontAwesomeIcon icon={faArrowRight} />
          </button>

          {!uproszczona && <SaveKitButton variant={v} requestPayload={requestPayload} nazwaWstepna={nazwaWstepna} />}
        </>
      )}
    </article>
  );
};

// Karty wyników nie zależą od stanu formularza — memo ogranicza re-render przy edycji.
export default React.memo(VariantCard);
